import {
  CountryEntry,
  countries,
  languages,
  useMutation,
  useMyUrl,
  useTranslation,
} from '@gimlite/common';
import {
  Col,
  FormType,
  Segmented,
  Widget,
  WidgetGroupType,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addCredGql } from '../../common/gql/add-cred.gql';
import { editCredGql } from '../../common/gql/edit-cred.gql';
import { regenQRGql } from '../../common/gql/regen-qr.gql';
import { updateContractGql } from '../../common/gql/update-contract.gql';
import { APBModeGlobal } from '../../common/mapper/abp.mapper';
import { downloadQrCode } from '../../common/mapper/credential.mapper';
import { iconGlobalEntity } from '../../common/mapper/icon.mapper';

import { ApolloError } from '@apollo/client';
import {
  AntiPassBackMode,
  ArticleCodeStatus,
  ContractEntity,
  Mutation,
  MutationAddCredOnContractArgs,
  MutationEditCredOnContractArgs,
  MutationRegenerateQrcodeArgs,
  MutationRemoveCredOnContractArgs,
  MutationUpdateContractArgs,
  ProductRules,
} from '../../client/graphql';
import { removeCredGql } from '../../common/gql/remove-cred.gql';
import {
  ContractBadgesType,
  useContractBadges,
} from '../../common/utils/contract-badges.hook';
import { CredentialCard } from '../../components/credential/credential.component';
import {
  ProfilDetails,
  ProfilDetailsType,
} from '../../components/profil-details/profil-details.component';
import { config } from '../../config';
import { MotoristContractAction } from '../action/motorist-contract.action';
import { PLATECredentialCard } from '../credential/plate.credential';
import { QRCODECredentialCard } from '../credential/qrcode.credential';
import { RFIDCredentialCard } from '../credential/rfid.credential';
import { TCSCredentialCard } from '../credential/tcs.credential';
import { ContractHistoryList } from '../list/history.list';
import { SessionList } from '../list/session.list';

type MotoristContractDetailsProps = {
  parkingId: string;
  contract: ContractEntity;
  widget?: {
    title?: string;
    state: WidgetGroupType.Props['state'];
  };
};

export function rules2APB({ cycleControl }: ProductRules): AntiPassBackMode {
  if (
    !cycleControl ||
    !cycleControl.pastDaysCount ||
    !cycleControl.maxOpenedSessions
  )
    return AntiPassBackMode.None;

  const { pastDaysCount, maxOpenedSessions } = cycleControl || {};
  if (pastDaysCount === 1 && maxOpenedSessions === 100)
    return AntiPassBackMode.Soft;
  if (pastDaysCount === 7 && maxOpenedSessions === 1)
    return AntiPassBackMode.Hard;
  return AntiPassBackMode.None;
}

export const MotoristContractDetails = observer(
  ({ widget, parkingId, contract }: MotoristContractDetailsProps) => {
    const { name1, name2, isEnabled, credentials, freeflag, motoristId } =
      contract;
    const { t, lang } = useTranslation();
    const [segmented, setSegmented] = useState<string>('credentials');
    const { setParamsUrl } = useMyUrl({});

    const [updateContractCall] = useMutation<
      { updateContract: Mutation['updateContract'] },
      MutationUpdateContractArgs
    >(updateContractGql);

    const [regenerateQrcodeCall] = useMutation<
      { regenerateQrcode: Mutation['regenerateQrcode'] },
      MutationRegenerateQrcodeArgs
    >(regenQRGql, {
      notification: {
        success: t('theQrcodeHasBeenRegenerated'),
        error: (error) =>
          error instanceof ApolloError
            ? t(`error-code-${error.message!}`)
            : `${error}`,
      },
    });

    const [addCredOnContractCall] = useMutation<
      { addCredOnContract: Mutation['addCredOnContract'] },
      MutationAddCredOnContractArgs
    >(addCredGql, {
      notification: {
        error: (e) => t(`error-code-${e.message}`),
      },
    });

    const [editCredOnContractCall] = useMutation<
      { editCredOnContract: Mutation['editCredOnContract'] },
      MutationEditCredOnContractArgs
    >(editCredGql, {
      notification: {
        error: (e) => t(`error-code-${e.message}`),
      },
    });

    const [removeCredOnContractCall] = useMutation<
      { removeCredOnContract: Mutation['removeCredOnContract'] },
      MutationRemoveCredOnContractArgs
    >(removeCredGql, {
      notification: {
        error: (e) => t(`error-code-${e.message}`),
      },
    });

    const credentialsTypes = contract?.product?.rules?.credentialsTypes
      ? contract.product.rules.credentialsTypes
      : null;

    const extractFormData = useCallback(
      (contract: ContractEntity): FormType.Data.Value => {
        const {
          name1,
          name2,
          ospContractId,
          presenceCounter,
          startDate,
          stopDate,
          freeflag,
          rules,
          description,
          reference,
          productId,
          articleCode,
          address,
          username,
          lang,
        } = contract;

        const freeFlagIsDefined = freeflag === null || freeflag === undefined;
        return {
          firstName: name1,
          lastName: name2,
          code: ospContractId,
          reference: reference,
          streetName: address?.address1,
          streetNumber: address?.address2,
          zipCode: address?.zipcode,
          noOfSpace: [`${presenceCounter || 0}`, `/1`],
          city: address?.city,
          country: address?.country,
          APBMode: rules ? rules2APB(rules!!) : 'NONE',
          APBNext: freeFlagIsDefined ? t('active') : t('suspended'),
          startDate,
          endDate: stopDate,
          description,
          isEnabled: contract.isEnabled,
          product: t(`product-${productId}-name`),
          articleCode: articleCode,
          username,
          lang,
        };
      },
      [lang],
    );

    const [form, setForm] = useState<FormType.Data.Value>({});

    const [data, setData] = useState<ContractEntity>(contract);
    const countryOptions = useMemo(() => {
      if (!countries || !lang || !countries[lang]) return [];

      return countries[lang].map(({ value, label }: CountryEntry) => ({
        label,
        value: value.ISO3,
      }));
    }, [countries, lang]);

    useEffect(() => {
      setSegmented('credentials');
    }, [contract]);

    useEffect(() => {
      setForm(extractFormData(contract));
    }, [lang, contract]);

    const handleSubmit = ({
      firstName,
      lastName,
      startDate,
      endDate,
      streetName,
      streetNumber,
      noOfSpace,
      city,
      country,
      zipCode,
      APBMode,
      description,
      reference,
      articleCode,
      motoristId,
      username,
      lang,
    }: FormType.Data.Value) => {
      const updatedContract = {
        name1: firstName || '',
        name2: lastName || '',
        startDate,
        stopDate: endDate,
        address: {
          address1: streetName,
          address2: streetNumber,
          city,
          country,
          zipcode: zipCode,
        },
        apbMode: APBMode,
        description,
        reference,
        presenceCounter: Number(noOfSpace[0]),
        articleCode,
        username,
        lang,
      } as MutationUpdateContractArgs['input'];

      updateContract(updatedContract);
    };

    const updateContract = async (
      updatedContract: MutationUpdateContractArgs['input'],
    ) => {
      if (data.isEnabled === updatedContract.isEnabled) return;

      setParamsUrl({ clicked: undefined });

      updateContractCall({
        variables: {
          contractId: contract._id,
          input: updatedContract,
        },
      }).then(({ data }) => {
        setData({ ...contract, ...data });
      });
    };

    // console.log({ credentials, credentialsTypes });

    // console.log('MERDIA,', [
    //   ...credentials!!,
    //   ...contract.motorist?.credentials.filter(
    //     ({ type, value }) =>
    //       type !== 'RFID' &&
    //       !credentials?.find((c) => c.value === value) &&
    //       !!credentialsTypes?.types?.find((t) => t.type === type),
    //   )!!,
    // ]);

    const credentialsRender = () => {
      const sortCredentials = [
        ...credentials!!.filter(
          ({ type, value, provider }) =>
            !!credentialsTypes?.types?.find((t) => {
              return t.type === 'PROVIDER_EXTERNAL_ID' && provider === 'OSP'
                ? false
                : t.type === 'PROVIDER_EXTERNAL_ID'
                  ? t.type === type &&
                    (t.provider || null) === (provider || null)
                  : t.type === type;
            }),
        )!!,
      ];

      return sortCredentials?.map(
        ({ type, value, provider, description, _id }) => {
          switch (type) {
            case 'PLATE':
              return (
                <PLATECredentialCard
                  handleEvent={{
                    event: async ({ event, primaryValue }) => {
                      if (event === 'unload') {
                        await editCredOnContractCall({
                          variables: {
                            contractId: contract._id,
                            credential: {
                              _id,
                              type,
                              value,
                              provider,
                              description,
                            },
                          },
                        });
                      }

                      if (event === 'import') {
                        await addCredOnContractCall({
                          variables: {
                            contractId: contract._id,
                            credential: {
                              type,
                              value,
                              provider,
                              description,
                            },
                          },
                        });
                      }

                      if (event === 'delete') {
                        await removeCredOnContractCall({
                          variables: {
                            contractId: contract._id,
                            credential: {
                              _id,
                              type,
                              value,
                              provider,
                              description,
                            },
                          },
                        });
                      }
                    },
                  }}
                  data={{ _id, description: description || '', plate: value }}
                  config={{
                    isActive: !!credentials?.find((c) => c.value === value),
                  }}
                />
              );

            case 'RFID':
              return (
                <RFIDCredentialCard
                  handleEvent={{
                    event: async ({ event, primaryValue }) => {
                      if (event === 'import') {
                        await addCredOnContractCall({
                          variables: {
                            contractId: contract._id,
                            credential: {
                              type,
                              value,
                              provider,
                              description,
                            },
                          },
                        });
                      }

                      if (event === 'delete') {
                        await removeCredOnContractCall({
                          variables: {
                            contractId: contract._id,
                            credential: {
                              _id,
                              type,
                              value,
                              provider,
                              description,
                            },
                          },
                        });
                      }
                    },
                  }}
                  data={{ _id, uid: value, visibleId: description || '' }}
                  config={{
                    isActive: !!credentials?.find((c) => c.value === value),
                  }}
                />
              );

            case 'QRCODE':
              return (
                <QRCODECredentialCard
                  handleEvent={{
                    event: async ({ event, name }) => {
                      if (
                        event === 're-generate' &&
                        name === 'QRCODE' &&
                        contract._id
                      ) {
                        console.log('MERDIA,', contract);
                        const { data } = await regenerateQrcodeCall({
                          variables: { contractId: contract._id },
                        });
                        console.log('MERDIA,', data);
                      } else if (event === 'download') {
                        await downloadQrCode({
                          contractId: contract._id!,
                          host: config.BFF_OSP_MOTORISTS_HOST!,
                          token: config.BFF_OSP_MOTORIST_TOKEN!,
                        });
                      }
                    },
                  }}
                  data={{
                    _id,
                    generatedAt: contract.qrcode?.createdAt
                      ? DateTime.fromISO(contract.qrcode?.createdAt).toFormat(
                          'dd/MM/yy HH:mm:ss',
                        )
                      : '',
                    value: value,
                  }}
                />
              );

            case 'PROVIDER_EXTERNAL_ID':
              switch (provider) {
                case 'OSP':
                  return (
                    <RFIDCredentialCard
                      handleEvent={{
                        event: async ({ event, primaryValue }) => {
                          if (event === 'unload') {
                            await editCredOnContractCall({
                              variables: {
                                contractId: contract._id,
                                credential: {
                                  _id,
                                  type,
                                  value,
                                  provider,
                                  description,
                                },
                              },
                            });
                          }
                          if (event === 'import') {
                            await addCredOnContractCall({
                              variables: {
                                contractId: contract._id,
                                credential: {
                                  type,
                                  value,
                                  provider,
                                  description,
                                },
                              },
                            });
                          }
                        },
                      }}
                      data={{ _id, uid: value, visibleId: description || '' }}
                      config={{
                        isActive: !!credentials?.find((c) => c.value === value),
                      }}
                    />
                  );

                case 'TCS':
                case 'TCSOSP':
                  return (
                    <TCSCredentialCard
                      handleEvent={{
                        event: async ({ event, primaryValue }) => {
                          if (event === 'unload') {
                            await editCredOnContractCall({
                              variables: {
                                contractId: contract._id,
                                credential: {
                                  _id,
                                  type,
                                  value,
                                  provider,
                                  description,
                                },
                              },
                            });
                          }
                          if (event === 'import') {
                            await addCredOnContractCall({
                              variables: {
                                contractId: contract._id,
                                credential: {
                                  type,
                                  value,
                                  provider,
                                  description,
                                },
                              },
                            });
                          }
                          if (event === 'delete') {
                            await removeCredOnContractCall({
                              variables: {
                                contractId: contract._id,
                                credential: {
                                  _id,
                                  type,
                                  value,
                                  provider,
                                  description,
                                },
                              },
                            });
                          }
                        },
                      }}
                      data={{ _id, externalId: value }}
                      config={{
                        isActive: !!credentials?.find((c) => c.value === value),
                      }}
                    />
                  );

                default:
                  return <></>;
              }

            default:
              return <></>;
          }
        },
      );
    };
    const status = isEnabled ? 'ACTIVE' : 'SUSPENDED';

    const isStopped = contract?.stopDate
      ? DateTime.fromISO(contract.stopDate).diffNow().as('milliseconds') < 0
      : false;
    const isUpcoming = DateTime.fromISO(contract.startDate) > DateTime.now();
    const states: [ContractBadgesType.Data.State] = [status];
    if (isUpcoming) states.push('UPCOMING');
    if (isStopped) states.push('EXPIRED');
    if (!!contract.presenceCounter && contract.presenceCounter > 0)
      states.push('PRESENT');
    if (isStopped || isUpcoming) states.shift();

    const badges = useContractBadges({ states });

    const profileDetails = () => {
      return (
        <Widget>
          <ProfilDetails
            handleEvent={{ submit: handleSubmit }}
            data={{
              icon:
                contract.category === 'MOTORIST_POOL'
                  ? iconGlobalEntity.motoristContractPool
                  : iconGlobalEntity.motoristContractSolo,
              form,
              badges: [
                ...badges,
                motoristId
                  ? {
                      text: t('verified'),
                      color: 'success',
                    }
                  : null,
              ].filter(Boolean) as ProfilDetailsType.Data.Badge[],
            }}
            config={{
              form: {
                title1: {
                  name: 'lastName',
                  placeholder: t('lastName'),
                  edit: !motoristId,
                },
                title2: {
                  name: 'firstName',
                  placeholder: t('firstName'),
                  edit: !motoristId,
                },
                description1: {
                  name: 'code',
                  placeholder: t('code'),
                  edit: false,
                },
                description2: {
                  name: 'reference',
                  placeholder: t('reference'),
                  edit: true,
                },
                group1: [
                  {
                    name: 'product',
                    label: t('product'),
                    element: {
                      type: 'input',
                    },
                    edit: false,
                  },
                  {
                    name: 'articleCode',
                    label: t('codePMS'),
                    element: {
                      type: 'select',
                      items: contract
                        .product!.externalPayload?.ArticleCodes?.filter(
                          (code) => code.status === ArticleCodeStatus.Active,
                        )
                        ?.map((code) => ({
                          label: code.code,
                          value: code.code,
                        })),
                    },
                    edit: true,
                  },
                  {
                    name: 'noOfSpace',
                    label: t('noOfSpace'),
                    element: { type: 'counter' },
                    render: (value = ['0', '0']) =>
                      `${value[0].replace('/', '')} / ${value[1].replace(
                        '/',
                        '',
                      )}`,
                  },
                  {
                    name: 'APBMode',
                    label: t('APBMode'),
                    tooltip: (
                      <Write
                        data={{
                          item: t('TOOLPIP_PRESENT_ANTIPASSBACK_MODE'),
                        }}
                        config={{ mode: 'key-small-bold' }}
                      />
                    ),
                    element: {
                      type: 'select',
                      items: APBModeGlobal.map((value) => ({
                        label: t(value),
                        value,
                      })),
                    },
                  },
                  {
                    name: 'APBNext',
                    label: t('APBNext'),
                    tooltip: (
                      <Write
                        data={{
                          item: t('TOOLPIP_PRESENT_ANTIPASSBACK_NEXT'),
                        }}
                        config={{ mode: 'key-small-bold' }}
                      />
                    ),
                    element: {
                      type: 'input',
                    },
                    color: !freeflag ? 'success' : 'warn',
                    edit: false,
                  },
                  {
                    name: 'startDate',
                    label: t('startDate'),
                    element: {
                      type: 'datetime',
                      min: contract.dateBoundaryFromParent?.from || null,
                      max:
                        contract.stopDate ||
                        contract.dateBoundaryFromParent?.to,
                    },
                    render: (value: string) =>
                      DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                  },
                  {
                    name: 'endDate',
                    label: t('endDate'),
                    element: {
                      type: 'datetime',
                      min:
                        contract.startDate ||
                        contract.dateBoundaryFromParent?.from,
                      max: contract.dateBoundaryFromParent?.to || null,
                    },
                    render: (value: string) =>
                      DateTime.fromISO(value).toFormat('dd/MM/yy HH:mm:ss'),
                  },
                  {
                    name: 'description',
                    label: t('description'),
                    element: { type: 'textarea' },
                  },
                ],
                group2: [
                  {
                    name: 'username',
                    label: t('email'),
                    element: { type: 'input' },
                    edit: !motoristId,
                  },
                  {
                    name: 'streetName',
                    label: t('address1'),
                    element: { type: 'input' },
                    edit: !motoristId,
                  },
                  {
                    name: 'streetNumber',
                    label: t('address2'),
                    element: { type: 'input' },
                    edit: !motoristId,
                  },
                  {
                    name: 'zipCode',
                    label: t('zipCode'),
                    element: { type: 'input' },
                    edit: !motoristId,
                  },
                  {
                    name: 'city',
                    label: t('city'),
                    element: { type: 'input' },
                    edit: !motoristId,
                  },
                  {
                    name: 'country',
                    label: t('country'),
                    element: {
                      type: 'select',
                      items: countryOptions,
                    },
                    edit: !motoristId,
                  },
                  {
                    name: 'lang',
                    label: t('language'),
                    element: {
                      type: 'select',
                      items: lang ? languages[lang] : [],
                    },
                    edit: !motoristId,
                  },
                ],
              },
              actions: (
                <MotoristContractAction
                  initialState={{
                    status: contract.isEnabled,
                    id: contract._id,
                    parkingId,
                    isStopped: contract.stopDate !== undefined && isStopped,
                    email: contract.username,
                    APBNext:
                      contract.freeflag === null ||
                      contract.freeflag === undefined,
                    category: contract.category,
                    motoristId: contract.motoristId,
                  }}
                  config={{ size: 'xlarge' }}
                  key={Math.random()}
                />
              ),
              edit:
                contract.category === 'MOTORIST_POOL' ||
                (contract.category === 'PERIOD_SUBSCRIPTION' && !isStopped),
            }}
          />
        </Widget>
      );
    };

    const motoristCredentials = () => (
      <Col config={{ scrollY: true, height: 'full' }}>
        <CredentialCard.Group
          handleEvent={{
            event: async ({ event, oldValue, newValue }) => {
              if (event === 'create') {
                switch (newValue.name) {
                  case 'RFID':
                    const { uid, visibleId } = newValue;

                    try {
                      await addCredOnContractCall({
                        variables: {
                          contractId: contract._id,
                          credential: {
                            _id: newValue._id,
                            type: 'RFID',
                            provider: 'OSP',
                            value: uid,
                            description: visibleId,
                          },
                        },
                      });
                    } catch (e) {
                      throw new Error('theCredentialAlreadyExists');
                    }

                    break;

                  case 'PLATE':
                    const { description, plate } = newValue;
                    try {
                      await addCredOnContractCall({
                        variables: {
                          contractId: contract._id,
                          credential: {
                            _id: newValue._id,
                            type: 'PLATE',
                            description,
                            value: plate,
                          },
                        },
                      });
                      break;
                    } catch (e) {
                      throw new Error('theCredentialAlreadyExists');
                    }
                  case 'TCS':
                    const { externalId, name, _id } = newValue;

                    try {
                      await addCredOnContractCall({
                        variables: {
                          contractId: contract._id,
                          credential: {
                            _id,
                            type: 'PROVIDER_EXTERNAL_ID',
                            provider: 'TCS',
                            value: externalId,
                          },
                        },
                      });
                    } catch (e) {
                      throw new Error('theCredentialAlreadyExists');
                    }
                    break;
                }
              } else if (event === 'edit') {
                switch (newValue.name) {
                  case 'PLATE':
                    try {
                      const { description, plate, _id } = newValue;

                      await editCredOnContractCall({
                        variables: {
                          contractId: contract._id,
                          credential: {
                            _id,
                            type: 'PLATE',
                            description,
                            value: plate,
                          },
                        },
                      });

                      break;
                    } catch (e: any) {
                      throw new Error(`error-code-${e.message}`);
                    }

                  case 'RFID':
                    try {
                      const { visibleId, uid, _id } = newValue;

                      await editCredOnContractCall({
                        variables: {
                          contractId: contract._id,
                          credential: {
                            _id,
                            type: 'RFID',
                            description: visibleId,
                            value: uid,
                          },
                        },
                      });

                      break;
                    } catch (e: any) {
                      throw new Error(`error-code-${e.message}`);
                    }
                }
              }

              return 'ok';
            },
          }}
          config={{
            place: isStopped
              ? 'contractDisabled'
              : motoristId
                ? 'contractWithMotorist'
                : 'contract',
            credentials: {
              RFID: !!credentialsTypes?.types?.find(
                ({ type, provider }) =>
                  type === 'RFID' ||
                  (type === 'PROVIDER_EXTERNAL_ID' && provider === 'OSP'),
              ),
              PLATE: !!credentialsTypes?.types?.find(
                ({ type }) => type === 'PLATE',
              ),
              TCS: !!credentialsTypes?.types?.find(({ type, provider }) =>
                type === 'PROVIDER_EXTERNAL_ID' && provider
                  ? ['TCSOSP', 'TCS'].includes(provider)
                  : false,
              ),
            },
          }}
        >
          {credentialsRender()}
        </CredentialCard.Group>
      </Col>
    );

    return (
      <Widget.Group
        config={{
          title: widget?.title,
          backtitle: !!widget?.title,
        }}
        state={widget?.state}
      >
        <Zone
          config={{
            zones: [['profil'], ['segmented'], ['details']],
            rows: ['min-content', 'min-content', '1fr'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area config={{ area: 'profil' }}>{profileDetails()}</Zone.Area>

          <Zone.Area config={{ area: 'segmented' }}>
            <Widget>
              <Segmented
                handleEvent={{
                  option: (value) => setSegmented(value.toString()),
                }}
                data={{ selected: segmented }}
                config={{
                  size: 'large',
                  options: [
                    { label: t('credentials'), value: 'credentials' },
                    { label: t('sessions'), value: 'sessions' },
                    { label: t('history'), value: 'history' },
                  ],
                }}
              />
            </Widget>
          </Zone.Area>

          <Zone.Area config={{ area: 'details' }}>
            <>
              {segmented === 'credentials' && motoristCredentials()}

              {segmented === 'sessions' && (
                <SessionList
                  parkingId={parkingId}
                  contractId={contract._id}
                  pageSize={15}
                />
              )}
              {segmented === 'history' && (
                <ContractHistoryList contractId={contract._id} />
              )}
            </>
          </Zone.Area>
        </Zone>
      </Widget.Group>
    );
  },
);
